import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Copy, Download, Link2 } from 'lucide-react';
import { API } from '../App';
import { useT } from '../i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

// All five work the same on the server (public_short_redirect).
export const LINK_ALIASES = ['r', 'c', 'go', 'click', 'track'];

/*
  Appends the "add to every link" text. A leading ? or & is optional; {n} is the
  link number (1, 2, 3…) and {domain} the host, e.g. "s=acc{n}" gives each ad
  account its own value of a parameter.
*/
export function applySuffix(url, suffix, n, host) {
  const s = String(suffix || '')
    .trim()
    .replace(/^[?&]+/, '')
    .replace(/\{n\}/g, String(n))
    .replace(/\{domain\}/g, host);
  if (!s) return url;
  return url + (url.includes('?') ? '&' : '?') + s;
}

/*
  One link per usable domain of the pool, in the pool order. A domain is usable
  when it is verified and not marked "unhealthy"; the others are returned in
  "skipped" with the reason. The pool strategy (round robin, weighted, sticky)
  does not matter here: every usable domain gets one link.
*/
export function buildPoolLinks({ pool, domains, trackingId, alias = 'r', suffix = '' }) {
  const byId = new Map((domains || []).map((d) => [d.id, d]));
  const usable = [];
  const skipped = [];
  for (const id of (pool && pool.domain_ids) || []) {
    const d = byId.get(id);
    if (!d) skipped.push({ id, hostname: id, reason: 'missing' });
    else if (d.status !== 'verified') skipped.push({ id, hostname: d.hostname, reason: 'unverified' });
    else if ((d.health_status || 'healthy') === 'unhealthy') skipped.push({ id, hostname: d.hostname, reason: 'unhealthy' });
    else usable.push(d);
  }
  const path = LINK_ALIASES.includes(alias) ? alias : 'r';
  const links = usable.map((d, i) => ({
    n: i + 1,
    hostname: d.hostname,
    url: applySuffix('https://' + d.hostname + '/' + path + '/' + trackingId, suffix, i + 1, d.hostname),
  }));
  return { links, skipped };
}

const csvCell = (v) => '"' + String(v).replace(/"/g, '""') + '"';

export function linksCsv(links) {
  return ['n,domain,link', ...links.map((l) => [l.n, csvCell(l.hostname), csvCell(l.url)].join(','))].join('\n') + '\n';
}

/*
  Campaign page: "Links for ad accounts". Picks a domain pool and lists one
  tracking link per healthy domain, to copy or download. Read only: nothing on
  the server changes. A campaign with its own domain answers 404 on any other
  domain (on purpose: the domain root and the SEO mode rely on it), so no pool
  links are given for it.
*/
export default function PoolLinkGenerator({ campaign, domains = [] }) {
  const { t } = useT();
  const [pools, setPools] = useState([]);
  const [poolsState, setPoolsState] = useState('loading');
  const [poolId, setPoolId] = useState('');
  const [alias, setAlias] = useState('r');
  const [suffix, setSuffix] = useState('');

  useEffect(() => {
    let alive = true;
    axios
      .get(API + '/domain-pools')
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setPools(list);
        setPoolId((cur) => cur || (list[0] ? list[0].id : ''));
        setPoolsState('ready');
      })
      .catch(() => alive && setPoolsState('error'));
    return () => {
      alive = false;
    };
  }, []);

  const trackingId = campaign && campaign.tracking_id;
  const boundDomain = campaign && campaign.domain_id ? (domains || []).find((d) => d.id === campaign.domain_id) : null;
  const pool = pools.find((p) => p.id === poolId);
  const { links, skipped } = useMemo(
    () => (pool && trackingId ? buildPoolLinks({ pool, domains, trackingId, alias, suffix }) : { links: [], skipped: [] }),
    [pool, domains, trackingId, alias, suffix]
  );

  const reasonText = (r) =>
    r === 'unhealthy' ? t('pulled out of rotation') : r === 'unverified' ? t('not verified') : t('deleted domain');

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(links.map((l) => l.url).join('\n'));
      toast.success(t('Links copied: {count}', { count: links.length }));
    } catch (_e) {
      toast.error(t('Could not copy the links'));
    }
  };

  const download = () => {
    const blob = new Blob([linksCsv(links)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'links-' + (trackingId || 'campaign') + '-' + ((pool && pool.name) || 'pool').replace(/[^\w-]+/g, '_') + '.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  };

  let body;
  if (campaign && campaign.domain_id) {
    body = (
      <div className="flex gap-2 rounded-md border border-border bg-surface-2 p-3 text-sm" data-testid="pool-links-bound">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <p>
          {t('This campaign has its own domain ({host}). On any other domain its links answer 404 — on purpose: the domain root and the SEO mode rely on it. Pool links are not given for such a campaign; use a campaign without its own domain.', {
            host: (boundDomain && boundDomain.hostname) || campaign.domain_id,
          })}
        </p>
      </div>
    );
  } else if (poolsState === 'loading') {
    body = <p className="text-sm text-muted-foreground">{t('Loading domain pools…')}</p>;
  } else if (poolsState === 'error') {
    body = (
      <p className="text-sm text-destructive" data-testid="pool-links-error">
        {t('Could not load the domain pools. Reload the page.')}
      </p>
    );
  } else if (!pools.length) {
    body = (
      <p className="text-sm text-muted-foreground" data-testid="pool-links-no-pools">
        {t('No domain pools yet. Create one on the Domains page, in the Domain pools section.')}
      </p>
    );
  } else {
    body = (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="space-y-1">
            <Label>{t('Domain pool')}</Label>
            <Select value={poolId} onValueChange={setPoolId}>
              <SelectTrigger data-testid="pool-links-pool">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pools.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>{t('Link path')}</Label>
            <Select value={alias} onValueChange={setAlias}>
              <SelectTrigger data-testid="pool-links-alias">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LINK_ALIASES.map((a) => (
                  <SelectItem key={a} value={a}>
                    {'/' + a + '/'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="pool-links-suffix">{t('Add to every link (optional)')}</Label>
            <Input
              id="pool-links-suffix"
              data-testid="pool-links-suffix"
              value={suffix}
              placeholder="s=acc{n}"
              onChange={(e) => setSuffix(e.target.value)}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {t('{n} is the link number (1, 2, 3…), {domain} is the domain. All link paths work the same.')}
        </p>
        {links.length ? (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={copyAll} data-testid="pool-links-copy">
                <Copy className="mr-2 h-4 w-4" />
                {t('Copy all')}
              </Button>
              <Button size="sm" variant="outline" onClick={download} data-testid="pool-links-csv">
                <Download className="mr-2 h-4 w-4" />
                {t('Download CSV')}
              </Button>
            </div>
            <ol className="space-y-1" data-testid="pool-links-list">
              {links.map((l) => (
                <li key={l.hostname} className="truncate rounded border border-border bg-surface-2 px-2 py-1 font-mono text-xs">
                  {l.url}
                </li>
              ))}
            </ol>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="pool-links-empty">
            {t('The pool has no verified healthy domains: there is nothing to give out.')}
          </p>
        )}
        {skipped.length ? (
          <div className="text-xs text-muted-foreground" data-testid="pool-links-skipped">
            <p className="font-medium">{t('Skipped domains:')}</p>
            <ul className="list-inside list-disc">
              {skipped.map((s) => (
                <li key={s.id}>
                  {s.hostname} — {reasonText(s.reason)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <p className="text-xs text-muted-foreground">
          {t('Pulling a domain out of rotation only removes it from this list: links already placed in ad accounts keep working.')}
        </p>
      </div>
    );
  }

  return (
    <Card data-testid="pool-links-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Link2 className="h-4 w-4" /> {t('Links for ad accounts')}
        </CardTitle>
        <CardDescription>
          {t('One tracking link per healthy domain of a pool — one link for each ad account.')}
        </CardDescription>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
